import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import MenuItem from '@mui/material/MenuItem';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useWizard } from '../../hooks/WizardProvider';
import StepPanel from '../../components/StepPanel';
import WizardFooter from '../../components/WizardFooter';
import Field from '../../components/ui/Field';

const APP_TYPES = ['whatsapp', 'gupshup'];

export default function Setup() {
  const { draft, setDraft, steps, stepErrors, currentStep, organisationId } = useWizard();
  const wa = draft.whatsapp;
  const savedToDb = Boolean(wa.mongo_id);

  const updateWa = (field, value) => {
    setDraft((prev) => ({
      ...prev,
      whatsapp: { ...prev.whatsapp, [field]: value },
    }));
  };

  const errorFields = [
    'organisation_id',
    'app_id',
    'app_name',
    'app_type',
    'wa_phone_number',
    'wa_phone_number_id',
    'waba_id',
    'user_access_token',
  ];
  const firstError = errorFields.map((k) => stepErrors[k]).find(Boolean);

  return (
    <>
      <StepPanel stepId={5} stepStatus={steps.whatsapp}>
        {firstError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {firstError}
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Enter WhatsApp Business API credentials. They are saved to WhatsAppBusinessApp when you
          continue.
          {organisationId ? ` Organisation: ${organisationId}` : ''}
        </Typography>

        {steps.whatsapp === 'skipped' ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            WhatsApp setup was skipped. Configure it later before using messaging campaigns.
          </Alert>
        ) : (
          <Alert severity="info" sx={{ mb: 2 }}>
            Need to set up campaigns first? Use <strong>Skip for now</strong> below and configure
            WhatsApp later.
          </Alert>
        )}

        <Alert
          severity={savedToDb ? 'success' : 'info'}
          icon={savedToDb ? <CheckCircleIcon /> : undefined}
          sx={{ mb: 2 }}
        >
          {savedToDb
            ? `Saved (app: ${wa.app_name || '—'}, number: ${wa.wa_phone_number || '—'})`
            : 'Not saved to database yet — complete this step to store configuration'}
        </Alert>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Field
              label="App ID"
              required
              value={wa.app_id}
              onChange={(e) => updateWa('app_id', e.target.value.replace(/\D/g, ''))}
              error={!!stepErrors.app_id}
              helperText={stepErrors.app_id}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Field
              label="App Name"
              required
              value={wa.app_name}
              onChange={(e) => updateWa('app_name', e.target.value)}
              error={!!stepErrors.app_name}
              helperText={stepErrors.app_name}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Field
              label="Phone Number ID"
              required
              value={wa.wa_phone_number_id}
              onChange={(e) => updateWa('wa_phone_number_id', e.target.value.replace(/\D/g, ''))}
              error={!!stepErrors.wa_phone_number_id}
              helperText={stepErrors.wa_phone_number_id}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Field
              label="WABA ID"
              required
              value={wa.waba_id}
              onChange={(e) => updateWa('waba_id', e.target.value)}
              error={!!stepErrors.waba_id}
              helperText={stepErrors.waba_id}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Field
              label="WhatsApp Number"
              required
              value={wa.wa_phone_number}
              onChange={(e) => updateWa('wa_phone_number', e.target.value.replace(/\D/g, ''))}
              error={!!stepErrors.wa_phone_number}
              helperText={stepErrors.wa_phone_number}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Field
              label="App Type"
              required
              select
              value={wa.app_type}
              onChange={(e) => updateWa('app_type', e.target.value)}
              error={!!stepErrors.app_type}
              helperText={stepErrors.app_type}
              InputLabelProps={{ shrink: true }}
            >
              {APP_TYPES.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </Field>
          </Grid>
          <Grid item xs={12}>
            <Field
              label="User Access Token"
              required
              type="password"
              value={wa.user_access_token}
              onChange={(e) => updateWa('user_access_token', e.target.value)}
              error={!!stepErrors.user_access_token}
              helperText={stepErrors.user_access_token}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={wa.is_active}
                  onChange={(e) => updateWa('is_active', e.target.checked)}
                />
              }
              label="Active"
            />
          </Grid>
        </Grid>
      </StepPanel>
      <WizardFooter stepId={currentStep} />
    </>
  );
}
